import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  Modal,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DeleteOrgModal from '../components/organization/DeleteOrgModal';
import AddAccountOwnerModal from '../components/organization/AddAccountOwnerModal';
import Util from '../util/util.js';
import { setOrganizationAccess } from '../util/storage';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const MonetaDashboard = ({ navigation, route }) => {
  const { t } = useTranslation('admin');

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeDrawings: 0,
    totalDrawings: 0,
    totalOrgs: 0,
  });
  const [organizations, setOrganizations] = useState([]);

  // Org being deleted via the confirmation modal (null when closed)
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Org being seeded an AccountOwner via the add-owner modal (null when closed)
  const [ownerTarget, setOwnerTarget] = useState(null);
  // Transient success banner after seeding an owner
  const [ownerAdded, setOwnerAdded] = useState(null);

  useEffect(() => {
    loadStats();
    // Refresh whenever the dashboard regains focus (e.g. returning from Create/Delete org or
    // Add Owner) so a newly created org appears immediately, without a manual app refresh.
    const unsubscribe = navigation.addListener?.('focus', loadStats);
    return unsubscribe;
  }, [navigation]);

  const loadStats = async () => {
    try {
      setLoading(true);

      // Fetch drawings and organizations in parallel, handling errors independently
      const [drawingsResult, orgsResult] = await Promise.allSettled([
        Util.get('Resty', '/items/drawings'),
        Util.get('Resty', '/items/organizations/all'),
      ]);

      if (drawingsResult.status === 'fulfilled' && drawingsResult.value && !drawingsResult.value.hasError) {
        const drawings = drawingsResult.value.items || [];
        const active = drawings.filter((d) => d.status === 'active').length;
        setStats((prev) => ({
          ...prev,
          activeDrawings: active,
          totalDrawings: drawings.length,
        }));
      }

      if (orgsResult.status === 'fulfilled' && orgsResult.value && !orgsResult.value.error) {
        const orgs = orgsResult.value.items || [];
        setOrganizations(orgs);
        setStats((prev) => ({
          ...prev,
          totalOrgs: orgs.length,
        }));
      } else {
        console.log('Orgs fetch failed:', orgsResult.status === 'rejected' ? orgsResult.reason : orgsResult.value?.error);
      }
    } catch (error) {
      console.log('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateTo = (screen) => {
    navigation.navigate(screen, { organization, admin, userAdmin, orgAdmin });
  };

  // Called by DeleteOrgModal after a successful delete
  const handleDeleted = async () => {
    setDeleteTarget(null);
    // The delete strips the org from the user's organization_access server-side, but the
    // locally-cached copy (used by the notifications list and org pickers) is now stale.
    // Re-pull it so the deleted org stops appearing as a phantom entry elsewhere.
    try {
      const response = await Util.get('Resty', '/items/getUserRoles');
      if (response.data?.organization_access) {
        await setOrganizationAccess(response.data.organization_access);
      }
    } catch (err) {
      console.log('Error refreshing org access after delete:', err);
    }
    loadStats();
  };

  // Called by AddAccountOwnerModal after a successful create
  const handleOwnerAdded = (ownerEmail) => {
    const org = ownerTarget?.organization;
    setOwnerTarget(null);
    setOwnerAdded({ email: ownerEmail, organization: org });
  };

  const renderCard = (title, description, onPress, icon) => (
    <Pressable
      style={({ hovered, focused, pressed }) => [
        styles.card,
        { cursor: 'pointer' },
        (hovered || focused) && styles.cardHovered,
        pressed && styles.cardPressed,
      ]}
      onPress={onPress}
    >
      <View style={styles.cardContent}>
        <CustomText style={styles.cardIcon}>{icon}</CustomText>
        <View style={styles.cardText}>
          <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
            {title}
          </CustomText>
          <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={{ marginTop: 2 }}>
            {description}
          </CustomText>
        </View>
      </View>
    </Pressable>
  );

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary}>
            {t('dashboard.title')}
          </CustomText>
          <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={styles.pageSubtitle}>
            {t('dashboard.subtitle')}
          </CustomText>
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
          </View>
        ) : (
          <>
            {/* Stats Section */}
            <View style={styles.statsCard}>
              <View style={styles.statsRow}>
                <View style={styles.statItem}>
                  <CustomText font="bold" size={32} color={AppStyles.colorSet.brandPrimary}>
                    {stats.totalOrgs}
                  </CustomText>
                  <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('dashboard.organizations')}
                  </CustomText>
                </View>
                <View style={styles.statDivider} />
                <View style={styles.statItem}>
                  <CustomText font="bold" size={32} color={AppStyles.colorSet.textPrimary}>
                    {stats.activeDrawings}
                  </CustomText>
                  <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('dashboard.activeDrawings')}
                  </CustomText>
                </View>
                <View style={styles.statDivider} />
                <View style={styles.statItem}>
                  <CustomText font="bold" size={32} color={AppStyles.colorSet.textPrimary}>
                    {stats.totalDrawings}
                  </CustomText>
                  <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('dashboard.totalDrawings')}
                  </CustomText>
                </View>
              </View>
            </View>

            {/* Quick Actions Section */}
            <View style={styles.section}>
              <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary} style={styles.sectionTitle}>
                {t('dashboard.quickActions')}
              </CustomText>

              {renderCard(
                t('dashboard.drawingManagement'),
                t('dashboard.drawingManagementSubtitle'),
                () => navigateTo('DrawingManagement'),
                '🎯'
              )}

              {renderCard(
                t('dashboard.createOrganization'),
                t('dashboard.createOrganizationSubtitle'),
                () => navigateTo('CreateParentOrg'),
                '🏢'
              )}

              {renderCard(
                t('dashboard.featureToggles'),
                t('dashboard.featureTogglesSubtitle'),
                () => navigateTo('MonetaFeatureToggles'),
                '🚩'
              )}
            </View>

            {/* Queries Section */}
            <View style={styles.section}>
              <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary} style={styles.sectionTitle}>
                {t('dashboard.queries')}
              </CustomText>

              {renderCard(
                t('dashboard.reportsByOrg'),
                t('dashboard.reportsByOrgSubtitle'),
                () => navigateTo('MonetaQueries'),
                '📊'
              )}
            </View>

            {/* Organizations Section */}
            <View style={styles.section}>
              <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary} style={styles.sectionTitle}>
                {t('dashboard.organizationsCount', { count: organizations.length })}
              </CustomText>

              {organizations.length === 0 ? (
                <View style={styles.emptyState}>
                  <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('dashboard.noOrganizations')}
                  </CustomText>
                </View>
              ) : (
                organizations.map((org) => (
                  <View key={org.organization} style={styles.orgCard}>
                    <View style={styles.orgCardMain}>
                      <View style={styles.orgHeader}>
                        <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                          {org.organization}
                        </CustomText>
                        {!org.parent && (
                          <View style={styles.parentBadge}>
                            <CustomText font="medium" size={11} color="#996600">
                              {t('dashboard.parent')}
                            </CustomText>
                          </View>
                        )}
                      </View>
                      {org.parent && (
                        <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                          {t('dashboard.childOf', { organization: org.parent })}
                        </CustomText>
                      )}
                      {org.allowed_email_domains && org.allowed_email_domains.length > 0 && (
                        <CustomText font="regular" size={12} color={AppStyles.colorSet.textTertiary} style={{ marginTop: 4 }}>
                          {t('dashboard.domainsList', { domains: org.allowed_email_domains.join(', ') })}
                        </CustomText>
                      )}
                    </View>

                    <Pressable
                      onPress={() => setOwnerTarget(org)}
                      style={styles.orgActionButton}
                      accessibilityRole="button"
                      accessibilityLabel={t('dashboard.addOwnerLabel', { organization: org.organization })}
                    >
                      <Feather name="user-plus" size={18} color={AppStyles.colorSet.textSecondary} />
                    </Pressable>
                    <Pressable
                      onPress={() => setDeleteTarget(org)}
                      style={styles.orgActionButton}
                      accessibilityRole="button"
                      accessibilityLabel={`Delete ${org.organization}`}
                    >
                      <Feather name="trash-2" size={18} color={AppStyles.colorSet.error} />
                    </Pressable>
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </ScrollView>
      {/* Delete confirmation modal (type-name confirmation) */}
      <DeleteOrgModal
        org={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      {/* Add Account Owner modal */}
      <AddAccountOwnerModal
        org={ownerTarget}
        onClose={() => setOwnerTarget(null)}
        onAdded={handleOwnerAdded}
      />

      {/* Success banner after seeding an owner */}
      <Modal
        visible={!!ownerAdded}
        transparent
        animationType="fade"
        onRequestClose={() => setOwnerAdded(null)}
      >
        <Pressable style={styles.successOverlay} onPress={() => setOwnerAdded(null)}>
          <Pressable style={styles.successCard} onPress={(e) => e.stopPropagation()}>
            <Feather name="check-circle" size={32} color={AppStyles.colorSet.brandPrimary} />
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary} style={styles.successTitle}>
              {t('dashboard.accountOwnerAdded')}
            </CustomText>
            <CustomText size={14} color={AppStyles.colorSet.textSecondary} style={styles.successText}>
              {t('dashboard.accountOwnerAddedBody', {
                email: ownerAdded?.email,
                organization: ownerAdded?.organization,
              })}
            </CustomText>
            <Pressable style={styles.successButton} onPress={() => setOwnerAdded(null)}>
              <CustomText size={14} font="medium" color={AppStyles.colorSet.textPrimary}>
                {t('common:actions.done')}
              </CustomText>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      <Footer
        organization={organization}
        admin={admin}
        userAdmin={userAdmin}
        orgAdmin={orgAdmin}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: 40,
    flexGrow: 1,
    alignItems: 'stretch',
  },
  pageHeader: {
    paddingHorizontal: 16,
    paddingTop: hp(2),
    paddingBottom: hp(1),
  },
  pageSubtitle: {
    marginTop: 4,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  statsCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    marginHorizontal: 16,
    marginBottom: hp(2),
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 20,
  },
  statDivider: {
    width: 1,
    height: 48,
    backgroundColor: AppStyles.colorSet.cardBorder,
  },
  section: {
    paddingHorizontal: 16,
  },
  sectionTitle: {
    marginBottom: 12,
  },
  card: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  // Web hover/focus feedback for admin nav cards
  cardHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    shadowOpacity: 0.1,
    shadowRadius: 12,
  },
  cardPressed: {
    backgroundColor: AppStyles.stateSet.pressed,
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardIcon: {
    fontSize: 36,
    marginRight: 14,
  },
  cardText: {
    flex: 1,
  },
  orgCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  orgCardMain: {
    flex: 1,
  },
  orgActionButton: {
    padding: 8,
    marginLeft: 4,
  },
  orgHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  parentBadge: {
    backgroundColor: 'rgba(255, 196, 81, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  emptyState: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  successOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  successCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  successTitle: {
    marginTop: 12,
    marginBottom: 8,
    textAlign: 'center',
  },
  successText: {
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  successButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
});

export default MonetaDashboard;
